import React, { useState } from 'react';
import { Mail, Lock, ShieldAlert, ArrowLeft, Cpu } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Button, Input } from '../ui';
import { getAuthErrorMessage } from '../../utils/errorHandler';
import { useNavigate } from 'react-router-dom';

import { signInWithEmailAndPassword } from 'firebase/auth';
import { auth, db } from '../../firebase/config';
import { doc, getDoc } from 'firebase/firestore';

interface SuperAdminLoginFormProps {
    onBack: () => void;
}

const SuperAdminLoginForm: React.FC<SuperAdminLoginFormProps> = ({ onBack }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Mark that this is a Super Admin Gateway Login attempt
        sessionStorage.setItem('superAdminGateway', 'true');
        sessionStorage.removeItem('authError');

        try {
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const firebaseUser = userCredential.user;

            // Check if onAuthStateChanged signed us out in the background
            const backgroundError = sessionStorage.getItem('authError');
            if (backgroundError) {
                setError(backgroundError);
                sessionStorage.removeItem('authError');
                setLoading(false);
                return;
            }

            // Fetch firestore document
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
            let role = '';
            if (userDoc.exists()) {
                role = userDoc.data().role;
            }

            if (role !== 'super_admin') {
                await auth.signOut();
                setError('Access Denied: This gateway is restricted to Super Administrators.');
                setLoading(false);
                return;
            }

            navigate('/dashboard', { replace: true });
        } catch (error: any) {
            setError(getAuthErrorMessage(error));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="w-full max-w-md mx-auto">
            {/* Cyber Dark Header */}
            <div className="text-center mb-8">
                <div className="flex items-center justify-center mb-4">
                    <div className="relative">
                        <div className="w-16 h-16 bg-slate-900 border-2 border-emerald-500 rounded-2xl flex items-center justify-center shadow-[0_0_20px_rgba(16,185,129,0.3)] animate-pulse">
                            <Cpu className="h-8 w-8 text-emerald-400" />
                        </div>
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-[0_0_10px_rgba(16,185,129,0.6)]">
                            <ShieldAlert className="h-3.5 w-3.5 text-slate-950 font-bold" />
                        </div>
                    </div>
                </div>
                <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 tracking-wider mb-2">
                    SUPER ADMIN
                </h2>
                <h3 className="text-xs uppercase tracking-[0.25em] text-emerald-400/80 font-bold">
                    Secure Control Gateway
                </h3>
                <div className="h-0.5 w-24 bg-gradient-to-r from-transparent via-emerald-500 to-transparent mx-auto mt-3"></div>
            </div>

            {/* Glowing Cyber Card */}
            <div className="bg-slate-950/85 border border-emerald-500/30 rounded-3xl p-6 shadow-[0_20px_50px_rgba(0,0,0,0.5),0_0_40px_rgba(16,185,129,0.1)] backdrop-blur-xl relative overflow-hidden">
                {/* Tech grid aesthetic overlay */}
                <div 
                    className="absolute inset-0 opacity-5 pointer-events-none" 
                    style={{
                        backgroundImage: `radial-gradient(circle at 1px 1px, #10b981 1px, transparent 0)`,
                        backgroundSize: '16px 16px'
                    }}
                ></div>

                <form onSubmit={handleSubmit} className="space-y-5 relative z-10">
                    {error && (
                        <div className="bg-red-950/50 border border-red-500/40 text-red-300 px-4 py-3 rounded-xl text-xs font-semibold shadow-[0_0_15px_rgba(239,68,68,0.1)]">
                            ⚠️ ERROR: {error}
                        </div>
                    )}

                    <div className="space-y-1">
                        <label className="block text-xs font-bold uppercase tracking-wider text-emerald-400/70 mb-1">
                            Secure ID (Email)
                        </label>
                        <div className="relative flex items-center">
                            <Mail className="absolute left-3.5 h-5 w-5 text-emerald-500/50" />
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="superadmin@edulink.com"
                                className="w-full pl-11 pr-4 py-2.5 bg-slate-900/60 border border-emerald-500/20 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all font-semibold text-sm"
                                required
                            />
                        </div>
                    </div>

                    <div className="space-y-1">
                        <label className="block text-xs font-bold uppercase tracking-wider text-emerald-400/70 mb-1">
                            Access Key (Password)
                        </label>
                        <div className="relative flex items-center">
                            <Lock className="absolute left-3.5 h-5 w-5 text-emerald-500/50" />
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="••••••••••••"
                                className="w-full pl-11 pr-4 py-2.5 bg-slate-900/60 border border-emerald-500/20 rounded-xl text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-all font-semibold text-sm"
                                required
                            />
                        </div>
                    </div>

                    <Button
                        type="submit"
                        loading={loading}
                        fullWidth
                        size="lg"
                        className="mt-6 !bg-gradient-to-r !from-emerald-500 !to-teal-600 hover:!from-emerald-600 hover:!to-teal-700 !text-slate-950 font-extrabold tracking-wider border-none shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_25px_rgba(16,185,129,0.5)] transition-all duration-300"
                    >
                        {loading ? 'INITIALIZING LINK...' : 'EXECUTE AUTHORIZATION'}
                    </Button>
                </form>

                {/* Back to normal portal */}
                <div className="mt-6 pt-5 border-t border-emerald-500/20 text-center relative z-10">
                    <button
                        type="button"
                        onClick={onBack}
                        className="inline-flex items-center space-x-2 text-xs font-bold uppercase tracking-wider text-emerald-400 hover:text-emerald-300 transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        <span>Return to Standard Portal</span>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default SuperAdminLoginForm;
